import React, { useCallback } from 'react';
import { Phone, Car, User, MapPin, Clock, CheckCircle } from 'lucide-react';
import { useUrbanrideComponentUpdates } from '../../contexts/SSEContext';

/**
 * Status timeline for vehicle tracking
 */
const VehicleStatusTimeline = ({ componentData, compact = false }) => {
  const statuses = [
    { code: 'VOL', label: 'Vehicle on Location', shortLabel: 'On Location', icon: MapPin, key: 'vehicle_vol_at' },
    { code: 'POB', label: 'Passenger on Board', shortLabel: 'On Board', icon: User, key: 'vehicle_pob_at' },
    { code: 'RC', label: 'Ride Completed', shortLabel: 'Completed', icon: CheckCircle, key: 'vehicle_rc_at' },
  ];

  const currentStatus = componentData?.vehicle_status;
  const statusIndex = statuses.findIndex(s => s.code === currentStatus);

  return (
    <div className={`flex items-center justify-between ${compact ? 'gap-2' : 'mt-4 pt-4 border-t border-ui-line'}`}>
      {statuses.map((status, index) => {
        const isCompleted = index <= statusIndex && statusIndex >= 0;
        const isCurrent = status.code === currentStatus;
        const timestamp = componentData?.[status.key];
        const Icon = status.icon;

        return (
          <div key={status.code} className="flex flex-col items-center flex-1">
            <div className={`
              ${compact ? 'w-8 h-8' : 'w-10 h-10'} rounded-full flex items-center justify-center transition-all
              ${isCompleted
                ? isCurrent ? 'bg-ui-green-strong text-ui-on-primary animate-pulse' : 'bg-ui-green-strong text-ui-on-primary'
                : 'bg-ui-line text-ui-muted'
              }
            `}>
              <Icon className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
            </div>
            <span className={`${compact ? 'text-xs' : 'text-xs'} mt-1 text-center ${isCompleted ? 'text-ui-green-fg font-medium' : 'text-ui-muted'}`}>
              {compact ? status.shortLabel : status.label}
            </span>
            {!compact && timestamp && (
              <span className="text-xs text-ui-muted">
                {new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Status badge component
 */
const StatusBadge = ({ status, statusName }) => {
  const statusStyles = {
    CNF: 'bg-ui-green-bg text-ui-green-fg',
    CNC: 'bg-ui-red-bg text-ui-red-fg',
    MOD: 'bg-ui-amber-bg text-ui-amber-fg',
    F: 'bg-ui-blue-bg text-ui-blue-fg',
    BIL: 'bg-ui-violet-bg text-ui-violet-fg',
  };

  return (
    <span className={`
      px-2 py-1 rounded-full text-xs font-medium
      ${statusStyles[status] || 'bg-ui-hover text-ui-ink'}
    `}>
      {statusName || status || 'Pending'}
    </span>
  );
};

/**
 * Compact chauffeur info for timeline cards
 */
export const ChauffeurStatusBadge = ({ componentData }) => {
  const hasChauffeur = componentData?.chauffeur_status === 'CA' && componentData?.chauffeur_name;
  const vehicleStatus = componentData?.vehicle_status;

  if (vehicleStatus === 'RC') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-ui-blue-bg text-ui-blue-fg">
        <CheckCircle className="w-3 h-3" />
        Completed
      </span>
    );
  }

  if (vehicleStatus === 'POB') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-ui-green-bg text-ui-green-fg animate-pulse">
        <Car className="w-3 h-3" />
        In Progress
      </span>
    );
  }

  if (vehicleStatus === 'VOL') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-ui-green-bg text-ui-green-fg">
        <MapPin className="w-3 h-3" />
        Driver Arrived
      </span>
    );
  }

  return null;
};

/**
 * Compact chauffeur card for timeline integration
 */
export const ChauffeurStatusCompact = ({ component, onUpdate }) => {
  const componentData = component?.component_data || {};

  // Subscribe to real-time updates for this component
  const handleUrbanrideEvent = useCallback((event) => {
    console.log('Urbanride event received:', event);
    if (onUpdate) {
      onUpdate();
    }
  }, [onUpdate]);

  useUrbanrideComponentUpdates(component?.id, handleUrbanrideEvent, []);

  const hasChauffeur = componentData.chauffeur_status === 'CA' && componentData.chauffeur_name;
  const hasVehicleStatus = componentData.vehicle_status;

  if (!hasChauffeur && !hasVehicleStatus) {
    return null;
  }

  return (
    <div className="mt-2 pt-2 border-t border-ui-divider">
      {hasChauffeur && (
        <div className="flex items-center gap-2 text-sm">
          <User className="w-4 h-4 text-ui-green-fg" />
          <span className="font-medium text-ui-faint">{componentData.chauffeur_name}</span>
          {componentData.chauffeur_mobile && (
            <a
              href={`tel:${componentData.chauffeur_mobile}`}
              className="flex items-center gap-1 text-ui-green-fg hover:text-ui-green-fg"
              onClick={(e) => e.stopPropagation()}
            >
              <Phone className="w-3.5 h-3.5" />
              <span>{componentData.chauffeur_mobile}</span>
            </a>
          )}
          {componentData.vehicle_plate && componentData.vehicle_plate !== 'NONE' && (
            <span className="text-ui-muted text-xs">
              <Car className="w-3 h-3 inline mr-1" />
              {componentData.vehicle_plate}
            </span>
          )}
        </div>
      )}
      {hasVehicleStatus && (
        <VehicleStatusTimeline componentData={componentData} compact={true} />
      )}
    </div>
  );
};

/**
 * ChauffeurStatusCard - Full status display component
 * Displays real-time chauffeur and vehicle status for Timeline 2.0 and TripDetails
 */
const ChauffeurStatusCard = ({ component, onUpdate }) => {
  const componentData = component?.component_data || {};

  // Subscribe to real-time updates for this component
  const handleUrbanrideEvent = useCallback((event) => {
    console.log('Urbanride event received:', event);
    // Trigger parent refresh to get updated component data
    if (onUpdate) {
      onUpdate();
    }
  }, [onUpdate]);

  useUrbanrideComponentUpdates(component?.id, handleUrbanrideEvent, []);

  const hasChauffeur = componentData.chauffeur_status === 'CA' && componentData.chauffeur_name;

  return (
    <div className="bg-ui-surface rounded-lg border border-ui-line p-4 shadow-sm">
      {/* Status Badge */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-ui-ink-2">Chauffeur Service Status</span>
        <StatusBadge
          status={componentData.urbanride_status}
          statusName={componentData.urbanride_status_name}
        />
      </div>

      {/* Chauffeur Details */}
      {hasChauffeur ? (
        <div className="bg-ui-green-bg border border-ui-green-line rounded-lg p-3">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-ui-green-line flex items-center justify-center flex-shrink-0">
              <User className="w-6 h-6 text-ui-green-fg" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-ui-ink">{componentData.chauffeur_name}</div>
              {componentData.chauffeur_mobile && (
                <a
                  href={`tel:${componentData.chauffeur_mobile}`}
                  className="flex items-center gap-1 text-sm text-ui-green-fg hover:text-ui-green-fg mt-1"
                >
                  <Phone className="w-4 h-4" />
                  {componentData.chauffeur_mobile}
                </a>
              )}
              {componentData.vehicle_plate && componentData.vehicle_plate !== 'NONE' && (
                <div className="flex items-center gap-1 text-sm text-ui-ink-3 mt-1">
                  <Car className="w-4 h-4" />
                  Plate: {componentData.vehicle_plate}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-ui-sunk border border-ui-line rounded-lg p-3">
          <div className="flex items-center gap-2 text-ui-muted">
            <Clock className="w-5 h-5" />
            <span className="text-sm">Chauffeur will be assigned closer to pickup time</span>
          </div>
        </div>
      )}

      {/* Vehicle Status Timeline */}
      {hasChauffeur && <VehicleStatusTimeline componentData={componentData} />}

      {/* Webhook History (collapsible for debugging) */}
      {componentData.webhook_history?.length > 0 && (
        <details className="mt-4 text-xs">
          <summary className="cursor-pointer text-ui-muted hover:text-ui-ink-2">
            Event History ({componentData.webhook_history.length})
          </summary>
          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
            {componentData.webhook_history.slice().reverse().map((event, idx) => (
              <div key={idx} className="flex justify-between text-ui-muted py-1 border-b border-ui-divider last:border-0">
                <span>{event.name || `${event.type}:${event.code}`}</span>
                <span>{event.timestamp ? new Date(event.timestamp).toLocaleString() : ''}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default ChauffeurStatusCard;
